import { useMemo, useState } from 'react';
import { ACTIVITY_ACTION_LABELS, activityLabel } from '@pointage/shared';
import {
  useActivityLog,
  useUsers,
  useEntities,
  type ActivityFilters,
  type ActivityLogEntryDTO,
} from '../api/hooks.js';

const isoDaysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const DAY_FMT = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
function dayLabel(iso: string): string {
  const today = new Date().toISOString().slice(0, 10);
  if (iso === today) return "Aujourd'hui";
  return DAY_FMT.format(new Date(`${iso}T12:00:00Z`));
}

function iconKind(action: string): 'ok' | 'warn' | 'info' {
  if (action.includes('delete') || action.includes('validate')) return 'warn';
  if (action.includes('import') || action.includes('send') || action.includes('generated')) return 'ok';
  return 'info';
}

export function ActivityPage() {
  const users = useUsers();
  const entities = useEntities();

  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const [entityId, setEntityId] = useState('');
  const [from, setFrom] = useState(isoDaysAgo(7));
  const [to, setTo] = useState(isoDaysAgo(0));

  const filters: ActivityFilters = useMemo(
    () => ({
      ...(userId ? { userId } : {}),
      ...(action ? { action } : {}),
      ...(entityId ? { entityId } : {}),
      from,
      to,
      limit: 500,
    }),
    [userId, action, entityId, from, to],
  );

  const log = useActivityLog(filters);
  const entries = log.data?.entries ?? [];

  // Le menu liste toutes les actions connues (une centaine) plus celles
  // vues dans la page. Il s'ordonne sur le LIBELLÉ affiché, pas sur la
  // clé technique: on y cherche « Congé », pas « leave. ».
  const actions = useMemo(() => {
    const set = new Set<string>(Object.keys(ACTIVITY_ACTION_LABELS));
    for (const e of entries) set.add(e.action);
    const labelOf = (a: string) => ACTIVITY_ACTION_LABELS[a] ?? a;
    return [...set].sort((a, b) => labelOf(a).localeCompare(labelOf(b), 'fr'));
  }, [entries]);

  // Group by calendar day (descending — entries already DESC by time).
  const grouped = useMemo(() => {
    const map = new Map<string, ActivityLogEntryDTO[]>();
    for (const e of entries) {
      const day = e.createdAt.slice(0, 10);
      const arr = map.get(day) ?? [];
      arr.push(e);
      map.set(day, arr);
    }
    return [...map.entries()];
  }, [entries]);

  const clear = () => { setUserId(''); setAction(''); setEntityId(''); setFrom(isoDaysAgo(7)); setTo(isoDaysAgo(0)); };

  const exportCsv = () => {
    const params = new URLSearchParams({ format: 'csv', from, to, limit: '5000' });
    if (userId) params.set('userId', userId);
    if (action) params.set('action', action);
    if (entityId) params.set('entityId', entityId);
    window.open(`/api/activity?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <div className="page-header">
        <div className="page-eyebrow">Journal d'audit</div>
        <h1 className="page-title">Activité <em>du groupe</em></h1>
        <p className="page-subtitle">
          Toutes les actions enregistrées sur les données — imports, modifications, envois d'email,
          générations de PDF. Visible par les BOSS et les administrateurs.
        </p>
      </div>

      <div className="filter-bar">
        <span className="label">Filtrer ·</span>
        <select className="set-input" value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="act-actor" aria-label="Acteur">
          <option value="">Tous les acteurs</option>
          {(users.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select className="set-input" value={action} onChange={(e) => setAction(e.target.value)} data-testid="act-action" aria-label="Action">
          <option value="">Toutes les actions</option>
          {actions.map((a) => <option key={a} value={a}>{ACTIVITY_ACTION_LABELS[a] ?? a}</option>)}
        </select>
        <select className="set-input" value={entityId} onChange={(e) => setEntityId(e.target.value)} data-testid="act-entity" aria-label="Entité">
          <option value="">Toutes les entités</option>
          {(entities.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <input className="set-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="act-from" aria-label="Du" />
        <input className="set-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="act-to" aria-label="Au" />
        <div className="toolbar-spacer" />
        <button className="btn btn-sm btn-ghost" onClick={clear} data-testid="act-clear">Effacer</button>
        <button className="btn btn-sm" onClick={exportCsv} data-testid="act-csv">Exporter CSV</button>
      </div>

      {log.isLoading && <p className="page-placeholder">Chargement…</p>}
      {log.isError && <p className="page-placeholder" data-testid="act-error">Impossible de charger le journal.</p>}

      {log.data && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid="act-list">
          {grouped.map(([day, rows]) => (
            <div key={day}>
              <div className="activity-day">{dayLabel(day)}</div>
              <div className="activity">
                {rows.map((e) => (
                  <div className="activity-row" key={e.id} data-testid={`act-row-${e.id}`}>
                    <div className="activity-time">{e.createdAt.slice(11, 16)}</div>
                    <div className={`activity-icon ${iconKind(e.action)}`} />
                    <div className="activity-text">
                      <strong>{e.user?.name ?? 'Système'}</strong> — {activityLabel(e)}
                    </div>
                    <div className="activity-user">{e.action}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {entries.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }} data-testid="act-empty">
              Aucune activité sur la période.
            </div>
          )}
        </div>
      )}
    </>
  );
}
